const API_BASE = '/api';

// Client version - must match backend APP_VERSION for seamless operation
// When backend version changes, clients with old version will be prompted to refresh
export const APP_VERSION = '2026-01-10';

// Auto-refresh on version mismatch - ensures users always have latest code
const triggerHardRefresh = () => {
  // Clear caches and force reload from server
  if ('caches' in window) {
    caches.keys().then(names => names.forEach(name => caches.delete(name)));
  }
  window.location.reload();
};

class ApiClient {
  private token: string | null = null;
  private versionChecked = false;

  setToken(token: string | null) {
    this.token = token;
    if (token) {
      localStorage.setItem('token', token);
    } else {
      localStorage.removeItem('token');
    }
  }

  getToken(): string | null {
    if (!this.token) {
      this.token = localStorage.getItem('token');
    }
    return this.token;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const token = this.getToken();
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    };

    if (token) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers,
    });

    // Check version on first API call - auto-refresh if mismatch
    if (!this.versionChecked) {
      const serverVersion = response.headers.get('X-App-Version');
      if (serverVersion && serverVersion !== APP_VERSION) {
        console.log(`Version mismatch: client=${APP_VERSION}, server=${serverVersion}. Refreshing...`);
        triggerHardRefresh();
        // Won't reach here - page will reload
      } else if (serverVersion) {
        this.versionChecked = true;
      }
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.error || 'Request failed');
    }

    return response.json();
  }

  // Auth
  async login(email: string, password: string) {
    return this.request<{ user: User; token: string }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  async register(name: string, email: string, password: string, gradeLevel: number) {
    return this.request<{ user: User; token: string }>('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ name, email, password, gradeLevel }),
    });
  }

  async getProfile() {
    return this.request<User>('/auth/me');
  }

  // Practice
  async startSession() {
    return this.request<{ sessionId: number; startedAt: string }>('/practice/sessions', {
      method: 'POST',
    });
  }

  async getNextQuestion(conceptId?: number, afterQuestionId?: number) {
    const params = new URLSearchParams();
    if (conceptId) params.append('conceptId', conceptId.toString());
    if (afterQuestionId) params.append('afterQuestionId', afterQuestionId.toString());
    const queryString = params.toString();
    const url = queryString ? `/practice/next-question?${queryString}` : '/practice/next-question';
    return this.request<QuestionResponse>(url);
  }

  // Get all seeded questions for a concept (for complete_all mode pre-loading)
  async getConceptQuestions(conceptId: number) {
    return this.request<{ questions: Array<QuestionResponse & { isCompleted?: boolean }> }>(`/practice/concept-questions/${conceptId}`);
  }

  async submitAnswer(questionId: number, answer: string, sessionId?: number) {
    return this.request<AnswerResult>('/practice/submit-answer', {
      method: 'POST',
      body: JSON.stringify({ questionId, answer, sessionId }),
    });
  }

  async startTutoring(questionId: number, conversationType: 'text' | 'voice' = 'text') {
    return this.request<{ conversationId: number; message: string }>('/practice/tutor/start', {
      method: 'POST',
      body: JSON.stringify({ questionId, conversationType }),
    });
  }

  async sendTutorMessage(
    conversationId: number,
    message: string,
    image?: string,
    conversationMode?: 'walkthrough' | 'demonstrate' | 'shortAnswer' | 'voice'
  ) {
    return this.request<{ message: string; isResolved: boolean }>('/practice/tutor/message', {
      method: 'POST',
      body: JSON.stringify({ conversationId, message, image, conversationMode }),
    });
  }

  async getConversation(questionId: number) {
    return this.request<{ conversationId: number | null; messages: Array<{ sender: string; text: string }> }>(
      `/practice/tutor/conversation/${questionId}`
    );
  }

  // OCR - convert handwritten math photo to LaTeX
  async ocrImage(imageBase64: string, mimeType?: string) {
    return this.request<{ latex: string; text: string; confidence: number }>('/practice/ocr', {
      method: 'POST',
      body: JSON.stringify({ image: imageBase64, mimeType }),
    });
  }

  async getOcrStatus() {
    return this.request<{ enabled: boolean }>('/practice/ocr/status');
  }

  // Cross-device photo sync
  async getCurrentQuestion() {
    return this.request<{ questionId: number | null; conceptId: number | null; timestamp: string | null }>('/practice/current-question');
  }

  async uploadPhoto(imageBase64: string, mimeType?: string) {
    return this.request<{ success: boolean; latex?: string; questionId: number | null; message: string }>('/practice/photo', {
      method: 'POST',
      body: JSON.stringify({ image: imageBase64, mimeType }),
    });
  }

  async getPendingPhotos() {
    return this.request<{ photos: PendingPhoto[]; questionId: number | null }>('/practice/photos/pending');
  }

  async clearPendingPhotos() {
    return this.request<{ success: boolean }>('/practice/photos/pending', {
      method: 'DELETE',
    });
  }

  async getSimilarProblem(questionId: number) {
    return this.request<SimilarProblemResponse>('/practice/similar-problem', {
      method: 'POST',
      body: JSON.stringify({ questionId }),
    });
  }

  async getFollowUpProblem(questionId: number) {
    return this.request<FollowUpResponse>('/practice/follow-up', {
      method: 'POST',
      body: JSON.stringify({ questionId }),
    });
  }

  async getWorkedExample(questionId: number) {
    return this.request<WorkedExampleResponse>('/practice/worked-example', {
      method: 'POST',
      body: JSON.stringify({ questionId }),
    });
  }

  async skipQuestion(questionId: number, conceptId: number, attemptCount: number) {
    return this.request<{ success: boolean; message: string }>('/practice/skip-question', {
      method: 'POST',
      body: JSON.stringify({ questionId, conceptId, attemptCount }),
    });
  }

  async getMastery() {
    return this.request<MasteryItem[]>('/practice/mastery');
  }

  async endSession(sessionId: number) {
    return this.request<SessionSummary>(`/practice/sessions/${sessionId}/end`, {
      method: 'POST',
    });
  }

  // Question History (persistent navigation)
  async getQuestionHistory(conceptId: number) {
    return this.request<{ history: QuestionHistoryEntry[] }>(`/practice/history/${conceptId}`);
  }

  async addToQuestionHistory(conceptId: number, questionId: number, isShowMe: boolean = false, afterPosition?: number) {
    return this.request<{ entry: QuestionHistoryEntry }>('/practice/history', {
      method: 'POST',
      body: JSON.stringify({ conceptId, questionId, isShowMe, afterPosition }),
    });
  }

  async updateQuestionHistory(conceptId: number, questionId: number, update: {
    state?: 'working' | 'completed' | 'skipped';
    attempts?: number;
    answerGiven?: string;
    isCorrect?: boolean;
  }) {
    return this.request<{ success: boolean }>(`/practice/history/${conceptId}/${questionId}`, {
      method: 'PATCH',
      body: JSON.stringify(update),
    });
  }

  async getHistoryProgress(conceptId: number) {
    return this.request<{ completed: number; total: number; consecutiveCorrect: number }>(`/practice/history/${conceptId}/progress`);
  }

  async clearQuestionHistory(conceptId: number) {
    return this.request<{ success: boolean }>(`/practice/history/${conceptId}`, {
      method: 'DELETE',
    });
  }

  // Teacher
  async getStudents() {
    return this.request<StudentSummary[]>('/teacher/students');
  }

  async addStudent(name: string, email: string, password: string, gradeLevel: number) {
    return this.request<{ id: number; name: string; email: string; gradeLevel: number }>('/teacher/students', {
      method: 'POST',
      body: JSON.stringify({ name, email, password, gradeLevel }),
    });
  }

  async updateStudent(studentId: number, updates: { name?: string; email?: string; gradeLevel?: number }) {
    return this.request<{ id: number; name: string; email: string; gradeLevel: number }>(`/teacher/students/${studentId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async getStudentDetail(studentId: number) {
    return this.request<StudentDetail>(`/teacher/students/${studentId}`);
  }

  async generateTest(studentId: number, questionCount?: number) {
    return this.request<GeneratedTest>('/teacher/tests/generate', {
      method: 'POST',
      body: JSON.stringify({ studentId, questionCount }),
    });
  }

  async getTests() {
    return this.request<TestSummary[]>('/teacher/tests');
  }

  async getTest(testId: number) {
    return this.request<TestDetail>(`/teacher/tests/${testId}`);
  }

  async gradeTest(testId: number, grades: GradeSubmission[]) {
    return this.request<GradeResult>(`/teacher/tests/${testId}/grade`, {
      method: 'POST',
      body: JSON.stringify({ grades }),
    });
  }

  // Classes
  async getClasses() {
    return this.request<ClassSummary[]>('/classes');
  }

  async getClass(classId: number) {
    return this.request<ClassDetail>(`/classes/${classId}`);
  }

  async createClass(name: string) {
    return this.request<ClassSummary>('/classes', {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  }

  async updateClass(classId: number, name: string) {
    return this.request<ClassSummary>(`/classes/${classId}`, {
      method: 'PUT',
      body: JSON.stringify({ name }),
    });
  }

  async deleteClass(classId: number) {
    return this.request<{ success: boolean }>(`/classes/${classId}`, {
      method: 'DELETE',
    });
  }

  async assignStudentsToClass(classId: number, studentIds: number[]) {
    return this.request<{ success: boolean; added: number }>(`/classes/${classId}/students`, {
      method: 'POST',
      body: JSON.stringify({ studentIds }),
    });
  }

  async removeStudentFromClass(classId: number, studentId: number) {
    return this.request<{ success: boolean }>(`/classes/${classId}/students/${studentId}`, {
      method: 'DELETE',
    });
  }

  async assignConceptsToClass(classId: number, conceptIds: number[]) {
    return this.request<{ success: boolean; added: number }>(`/classes/${classId}/concepts`, {
      method: 'POST',
      body: JSON.stringify({ conceptIds }),
    });
  }

  async removeConceptFromClass(classId: number, conceptId: number) {
    return this.request<{ success: boolean }>(`/classes/${classId}/concepts/${conceptId}`, {
      method: 'DELETE',
    });
  }

  async getAvailableConcepts(classId: number) {
    return this.request<AvailableConcept[]>(`/classes/${classId}/available-concepts`);
  }

  // Gamification API
  async getStreak() {
    return this.request<StreakInfo>('/gamification/streak');
  }

  async getBadges() {
    return this.request<BadgesResponse>('/gamification/badges');
  }

  async checkBadges() {
    return this.request<BadgeCheckResponse>('/gamification/badges/check', {
      method: 'POST',
    });
  }

  async getLeaderboard() {
    return this.request<{ leaderboard: LeaderboardEntry[] }>('/gamification/leaderboard');
  }

  // Voice API
  async getVoiceStatus() {
    return this.request<VoiceStatus>('/voice/status');
  }

  async textToSpeech(text: string): Promise<ArrayBuffer | null> {
    const token = this.getToken();
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    if (token) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
    }

    try {
      const response = await fetch(`${API_BASE}/voice/tts`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ text }),
      });

      if (!response.ok) {
        console.error('TTS request failed:', response.status);
        return null;
      }

      return await response.arrayBuffer();
    } catch (error) {
      console.error('TTS error:', error);
      return null;
    }
  }

  async speechToText(audioBlob: Blob): Promise<string | null> {
    const token = this.getToken();
    const formData = new FormData();
    formData.append('audio', audioBlob, 'recording.webm');

    try {
      const response = await fetch(`${API_BASE}/voice/stt`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        body: formData,
      });

      if (!response.ok) {
        console.error('STT request failed:', response.status);
        return null;
      }

      const data = await response.json();
      return data.transcript;
    } catch (error) {
      console.error('STT error:', error);
      return null;
    }
  }

  /**
   * Get text chunked into voice phrases for interruption handling
   */
  async getVoicePhrases(text: string): Promise<VoicePhrase[]> {
    return this.request<{ phrases: VoicePhrase[] }>('/voice/tts/chunked', {
      method: 'POST',
      body: JSON.stringify({ text }),
    }).then(res => res.phrases);
  }

  /**
   * Generate TTS audio for a single phrase
   */
  async speakPhrase(text: string): Promise<ArrayBuffer | null> {
    const token = this.getToken();
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    if (token) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
    }

    try {
      const response = await fetch(`${API_BASE}/voice/tts/phrase`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ text }),
      });

      if (!response.ok) {
        console.error('Phrase TTS request failed:', response.status);
        return null;
      }

      return await response.arrayBuffer();
    } catch (error) {
      console.error('Phrase TTS error:', error);
      return null;
    }
  }

  // ============================================================================
  // LESSON MANAGEMENT (Teacher only)
  // ============================================================================

  async getLessons() {
    return this.request<Lesson[]>('/teacher/lessons');
  }

  async getLesson(id: number) {
    return this.request<LessonDetail>(`/teacher/lessons/${id}`);
  }

  async createLesson(lesson: CreateLessonRequest) {
    return this.request<Lesson>('/teacher/lessons', {
      method: 'POST',
      body: JSON.stringify(lesson),
    });
  }

  async updateLesson(id: number, lesson: Partial<CreateLessonRequest>) {
    return this.request<Lesson>(`/teacher/lessons/${id}`, {
      method: 'PUT',
      body: JSON.stringify(lesson),
    });
  }

  async deleteLesson(id: number) {
    return this.request<{ success: boolean; deletedId: number }>(`/teacher/lessons/${id}`, {
      method: 'DELETE',
    });
  }

  async addQuestion(lessonId: number, question: CreateQuestionRequest) {
    return this.request<Question>(`/teacher/lessons/${lessonId}/questions`, {
      method: 'POST',
      body: JSON.stringify(question),
    });
  }

  async updateQuestion(id: number, question: Partial<CreateQuestionRequest>) {
    return this.request<Question>(`/teacher/questions/${id}`, {
      method: 'PUT',
      body: JSON.stringify(question),
    });
  }

  async deleteQuestion(id: number) {
    return this.request<{ success: boolean; deletedId: number }>(`/teacher/questions/${id}`, {
      method: 'DELETE',
    });
  }

  async bulkImportQuestions(lessonId: number, questions: CreateQuestionRequest[]) {
    return this.request<{ success: boolean; insertedCount: number; questionIds: number[] }>(
      `/teacher/lessons/${lessonId}/questions/bulk`,
      {
        method: 'POST',
        body: JSON.stringify({ questions }),
      }
    );
  }
}

// Types
export interface User {
  id: number;
  name: string;
  email: string;
  gradeLevel: number;
  xpPoints?: number;
  masteredConcepts?: number;
  totalAttempts?: number;
  isTeacher: boolean;
}

export interface QuestionResponse {
  questionId?: number;
  questionText?: string;
  concept?: {
    id: number;
    title: string;
    lessonNumber: number;
    dueBy?: string;
    completionCriteria?: 'two_first_try' | 'complete_all';
    consecutiveCorrect?: number;
    totalQuestions?: number;
    totalCompleted?: number;
  };
  complete?: boolean;
  message?: string;
}

export interface AnswerResult {
  isCorrect: boolean;
  feedback: string;
  message?: string;           // Tutor's response (unified model)
  conversationId?: number;    // For continuing chat
  needsExplanation?: boolean; // Whether student needs to explain further
  xpEarned: number;
  attemptCount?: number;
}

export interface SimilarProblemResponse {
  questionId: number;
  questionText: string;
  isSimilarProblem: boolean;
  originalQuestionId: number;
}

export interface FollowUpResponse {
  questionId: number;
  questionText: string;
  isFollowUp: boolean;
  originalQuestionId: number;
}

export interface WorkedExampleResponse {
  steps: string[];
  finalAnswer: string;
}

export interface MasteryItem {
  conceptId: number;
  lessonNumber: number;
  title: string;
  dueBy?: string;
  masteryLevel: number;
  totalAttempts: number;
  consecutiveCorrect: number;
  verifiedByTest: boolean;
}

export interface QuestionHistoryEntry {
  id: number;
  questionId: number;
  conceptId: number;
  position: number;
  state: 'working' | 'completed' | 'skipped';
  attempts: number;
  isShowMe: boolean;
  answerGiven?: string;
  isCorrect?: boolean;
  createdAt: string;
  updatedAt: string;
  questionText?: string;
  correctAnswer?: string;
  lessonNumber?: number;
  title?: string;
  completionCriteria?: string;
  totalQuestions?: number;
  totalCompleted?: number;
}

export interface SessionSummary {
  sessionId: number;
  questionsAttempted: number;
  questionsCorrect: number;
  xpEarned: number;
  duration: number;
}

export interface StudentSummary {
  id: number;
  name: string;
  email: string;
  gradeLevel: number;
  xpPoints: number;
  lastLogin: string | null;
  masteredConcepts: number;
  totalAttempts: number;
  accuracy: number;
}

export interface StudentDetail {
  student: StudentSummary;
  mastery: MasteryItem[];
  recentActivity: Array<{
    timestamp: string;
    isCorrect: boolean;
    question: string;
    concept: string;
  }>;
}

export interface GeneratedTest {
  assessmentId: number;
  studentId: number;
  questionCount: number;
  questions: Array<{ id: number; questionText: string; order: number }>;
}

export interface TestSummary {
  id: number;
  studentId: number;
  studentName: string;
  date: string;
  questionCount: number;
  graded: boolean;
  score: number | null;
}

export interface TestDetail {
  id: number;
  studentId: number;
  studentName: string;
  date: string;
  graded: boolean;
  score: number | null;
  questions: Array<{
    id: number;
    questionId: number;
    order: number;
    questionText: string;
    correctAnswer: string;
    concept: string;
    studentAnswer: string | null;
    isCorrect: boolean | null;
    pointsEarned: number | null;
  }>;
}

export interface GradeSubmission {
  questionId: number;
  studentAnswer: string;
  isCorrect: boolean;
  pointsEarned?: number;
}

export interface GradeResult {
  assessmentId: number;
  scorePercentage: number;
  totalEarned: number;
  totalPossible: number;
  verified: boolean;
}

export interface ClassSummary {
  id: number;
  name: string;
  teacher_id: number;
  created_at: string;
  student_count: number;
  concept_count: number;
}

export interface ClassDetail {
  id: number;
  name: string;
  teacher_id: number;
  created_at: string;
  students: Array<{ id: number; name: string; email: string; grade_level: number }>;
  concepts: Array<{ id: number; lesson_number: number; title: string }>;
}

export interface AvailableConcept {
  id: number;
  lesson_number: number;
  title: string;
  curriculum: string;
}

// Gamification types
export interface StreakInfo {
  currentStreak: number;
  longestStreak: number;
  lastActivityDate: string | null;
  streakFreezesAvailable: number;
  totalPracticeDays: number;
  isActiveToday: boolean;
}

export interface Badge {
  id: number;
  code: string;
  title: string;
  description: string;
  xpReward: number;
  earned: boolean;
  earnedAt: string | null;
}

export interface BadgesResponse {
  badges: Badge[];
  earnedCount: number;
  totalCount: number;
}

export interface NewBadge {
  code: string;
  title: string;
  xpReward: number;
}

export interface BadgeCheckResponse {
  newBadges: NewBadge[];
  message: string;
}

export interface LeaderboardEntry {
  rank: number;
  id: number;
  name: string;
  xpPoints: number;
  currentStreak: number;
  weeklyCorrect: number;
}

export interface VoiceStatus {
  tts: boolean;
  stt: boolean;
}

export interface VoicePhrase {
  text: string;
  priority: 1 | 2 | 3;
  canInterrupt: boolean;
}

export interface PendingPhoto {
  imageBase64: string;
  latex?: string;
  timestamp: string;
}

// Lesson Management Types
export interface Lesson {
  id: number;
  lessonNumber: number;
  title: string;
  description?: string;
  gradeLevel: number;
  curriculum: string;
  prerequisites: number[];
  questionCount: number;
  createdAt: string;
}

export interface Question {
  id: number;
  conceptId?: number;
  questionText: string;
  correctAnswer: string;
  hints: string[];
  explanation?: string;
  difficultyLevel: number;
  approvedForTests: boolean;
  timesUsed?: number;
  timesCorrect?: number;
}

export interface LessonDetail extends Omit<Lesson, 'questionCount'> {
  questions: Question[];
}

export interface CreateLessonRequest {
  lessonNumber: number;
  title: string;
  description?: string;
  gradeLevel: number;
  curriculum?: string;
  prerequisites?: number[];
}

export interface CreateQuestionRequest {
  questionText: string;
  correctAnswer: string;
  hints?: string[];
  explanation?: string;
  difficultyLevel?: number;
  approvedForTests?: boolean;
}

export const api = new ApiClient();
